/**
 * The field mapping of one Veeva connection (Developer+; ADR-0035).
 *
 * Every eTMF vault is configured differently, so which document field holds
 * the study, country, site, artifact, zone, document date and status is
 * configurable once per connection. Saving creates a new version (audited);
 * the defaults are the standard Veeva eTMF fields. Read-only for a User.
 */

import { useEffect, useState } from "react";
import {
  Button,
  Group,
  Modal,
  NumberInput,
  SimpleGrid,
  Stack,
  TagsInput,
  Text,
  TextInput,
} from "@mantine/core";
import { useAccessToken } from "@/features/auth/useAccessToken";
import { saveEtmfMapping, type EtmfMappingOut } from "@/shared/api/etmfCompleteness";
import { toastApiError, toastSuccess } from "@/shared/ui/toast";

interface Props {
  opened: boolean;
  onClose: () => void;
  mapping: EtmfMappingOut;
  canEdit: boolean;
  onSaved: (m: EtmfMappingOut) => void;
}

export function MappingEditor({ opened, onClose, mapping, canEdit, onSaved }: Props) {
  const getToken = useAccessToken();
  const [fields, setFields] = useState<Record<string, string>>(mapping.fields);
  const [statuses, setStatuses] = useState<string[]>(mapping.filed_statuses);
  const [grace, setGrace] = useState<number>(mapping.grace_days);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!opened) return;
    setFields(mapping.fields);
    setStatuses(mapping.filed_statuses);
    setGrace(mapping.grace_days);
  }, [opened, mapping]);

  const onSave = async () => {
    setSaving(true);
    try {
      const saved = await saveEtmfMapping(await getToken(), mapping.connection_id, {
        fields,
        filed_statuses: statuses,
        grace_days: grace,
        expected_version: mapping.version,
      });
      toastSuccess("Mapping saved", `Version ${saved.version}. Refresh to recompute.`);
      onSaved(saved);
      onClose();
    } catch (err) {
      toastApiError("Could not save the mapping", err);
    } finally {
      setSaving(false);
    }
  };

  const resetDefaults = () => {
    setFields(Object.fromEntries(mapping.field_definitions.map((d) => [d.key, d.default])));
    setStatuses(mapping.default_filed_statuses);
    setGrace(0);
  };

  return (
    <Modal opened={opened} onClose={onClose} title="Vault field mapping" size="lg">
      <Stack gap="sm">
        <Text size="sm" c="dimmed">
          Which document field in this vault holds each value. Use the field&apos;s API name (for
          example <code>study__v</code>). Leave TMF zone empty to derive it from the artifact
          number.{" "}
          {mapping.version === 0
            ? "Not saved yet — the defaults apply."
            : `Version ${mapping.version}.`}
        </Text>
        <SimpleGrid cols={{ base: 1, sm: 2 }}>
          {mapping.field_definitions.map((d) => (
            <TextInput
              key={d.key}
              label={d.label}
              description={`Default: ${d.default || "derived"}`}
              required={d.required}
              value={fields[d.key] ?? ""}
              disabled={!canEdit}
              onChange={(e) => {
                const v = e.currentTarget.value;
                setFields((cur) => ({ ...cur, [d.key]: v }));
              }}
            />
          ))}
        </SimpleGrid>
        <TagsInput
          label="Statuses that count as filed"
          description="Empty = any status counts. Matching ignores case."
          value={statuses}
          onChange={setStatuses}
          disabled={!canEdit}
        />
        <NumberInput
          label="Grace days"
          description="A document filed this many days after its due date is still on time."
          min={0}
          max={365}
          value={grace}
          onChange={(v) => setGrace(typeof v === "number" ? v : 0)}
          disabled={!canEdit}
          w={220}
        />
        {!canEdit && (
          <Text size="sm" c="dimmed">
            Only a Developer or Administrator can change the mapping.
          </Text>
        )}
        <Group justify="space-between">
          <Button variant="subtle" onClick={resetDefaults} disabled={!canEdit}>
            Reset to defaults
          </Button>
          <Group gap="xs">
            <Button variant="default" onClick={onClose}>
              Close
            </Button>
            {canEdit && (
              <Button onClick={() => void onSave()} loading={saving}>
                Save mapping
              </Button>
            )}
          </Group>
        </Group>
      </Stack>
    </Modal>
  );
}
